import { Routes } from '@angular/router';
import { AuthGuard } from './shared/guards/auth.guard';
import { RoleGuard } from './shared/guards/role.guard';
import { NoAuthGuard } from './shared/guards/no-auth.guard';
import { DefaultRouteGuard } from './shared/guards/default-route.guard';
import { InactiveAccountGuard } from './shared/guards/inactive-account.guard';
import { UserRole, UserRoleAll, UserRolesWithLister } from './shared/models/user';
import { ShopifyGuard } from './shared/guards/shopify.guard';
import { orderRoutes } from '@/order/order-routes';
import { packingRoutes } from '@/packing/packing-routes';
import { shipmentsRoutes } from '@/shipments/shipments-routes';
import { shippingRoutes } from '@/shipping/shipping-routes';
import { settingsRoutes } from '@/settings/settings-routes';
import { pickingRoutes } from '@/picking/picking-routes';
import { pickupRoutes } from '@/pickup/pickup-routes';
import { dashboardRoutes } from '@/dashboard/dashboard-routes';
import { archiveRoutes } from '@/archive/archive-routes';
import { waybillRoutes } from '@/waybill/waybill-routes';
import { adminRoutes } from '@/admin/admin-routes';
import { inventoryRoutes } from '@/inventory/inventory-routes';
import { InventoryEnabledGuard } from './shared/guards/inventory-enabled.guard';
import { LocalPickupScreenEnabledGuard } from './shared/guards/local-pickup-screen-enabled.guard';
import { CustomerCrmEnabledGuard } from './shared/guards/customer-crm-enabled.guard';

export const appRoutes: Routes = [
  {
    path: '',
    canActivate: [DefaultRouteGuard],
    children: [],
  },
  {
    path: 'login',
    loadComponent: () => import('./core/login/login.component').then((m) => m.LoginComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'login-shopify',
    loadComponent: () => import('./core/login-shopify/login-shopify.component').then((m) => m.LoginShopifyComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'shopify',
    loadComponent: () =>
      import('./core/install-shopify/install-shopify.component').then((m) => m.InstallShopifyComponent),
    canActivate: [ShopifyGuard],
  },
  {
    path: 'register',
    loadComponent: () => import('./core/register/register.component').then((m) => m.RegisterComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'register-shopify',
    loadComponent: () =>
      import('./core/register-shopify/register-shopify.component').then((m) => m.RegisterShopifyComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./core/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'reset-password/:token',
    loadComponent: () =>
      import('./core/create-new-password/create-new-password.component').then((m) => m.CreateNewPasswordComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'join-company/:token',
    loadComponent: () => import('./core/join-company/join-company-component').then((m) => m.JoinCompanyComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'reset-password-sent',
    loadComponent: () =>
      import('./core/email-sent-confirmation/email-sent-confirmation.component').then(
        (m) => m.EmailSentConfirmationComponent
      ),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'connect-discogs',
    loadComponent: () =>
      import('./core/connect-discogs/connect-discogs.component').then((m) => m.ConnectDiscogsComponent),
    canActivate: [AuthGuard, InactiveAccountGuard],
  },
  {
    path: 'onboarding/success',
    loadComponent: () =>
      import('./core/onboarding-success/onboarding-success.component').then((m) => m.OnboardingSuccessComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'onboarding/cancel',
    loadComponent: () =>
      import('./core/onboarding-cancel/onboarding-cancel.component').then((m) => m.OnboardingCancelComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'email-verified',
    loadComponent: () => import('./core/email-verified/email-verified.component').then((m) => m.EmailVerifiedComponent),
  },
  {
    path: 'get-started',
    loadComponent: () => import('./get-started/get-started.component').then((m) => m.GetStartedComponent),
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: [UserRole.admin] },
  },
  {
    path: 'plans',
    loadComponent: () => import('./plans/plans.component').then((m) => m.PlansComponent),
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    // CRAT-1360 — plan selection, trial start and the Stripe portal are company-admin actions (the API enforces it too).
    data: { allowedRoles: [UserRole.admin] },
  },
  {
    path: 'inventory',
    canActivate: [AuthGuard, RoleGuard, InventoryEnabledGuard, InactiveAccountGuard],
    // CRAT-975 — Listers reach the inventory tree (their only working area); the table/collections/
    // sources children are restricted back to non-Listers inside inventoryRoutes.
    data: { allowedRoles: UserRolesWithLister },
    children: inventoryRoutes,
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: dashboardRoutes,
  },
  {
    path: 'orders',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: orderRoutes,
  },
  {
    path: 'customers',
    canActivate: [AuthGuard, RoleGuard, CustomerCrmEnabledGuard, InactiveAccountGuard],
    data: { allowedRoles: [UserRole.admin] },
    loadComponent: () =>
      import('./customers/customers-table/customers-table.component').then((m) => m.CustomersTableComponent),
  },
  {
    path: 'customers/:id',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    loadComponent: () =>
      import('./customers/customer-detail/customer-detail.component').then((m) => m.CustomerDetailComponent),
  },
  {
    path: 'picking',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: pickingRoutes,
  },
  {
    path: 'packing',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: packingRoutes,
  },
  {
    path: 'pickup',
    canActivate: [AuthGuard, RoleGuard, LocalPickupScreenEnabledGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: pickupRoutes,
  },
  {
    path: 'shipments',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: shipmentsRoutes,
  },
  {
    path: 'waybills',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: waybillRoutes,
  },
  {
    path: 'shipping',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: shippingRoutes,
  },
  {
    path: 'archive',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRoleAll },
    children: archiveRoutes,
  },
  {
    path: 'settings',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    // CRAT-975 — Listers can reach Settings (user settings only; the admin panel is gated by isAdmin).
    data: { allowedRoles: UserRolesWithLister },
    children: settingsRoutes,
  },
  {
    // CRAT-1302 — every role including lister; the inbox is per-user, not tier-gated.
    path: 'notifications',
    loadComponent: () =>
      import('./notifications/notifications-page.component').then((m) => m.NotificationsPageComponent),
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: UserRolesWithLister },
  },
  {
    path: 'admin',
    canActivate: [AuthGuard, RoleGuard, InactiveAccountGuard],
    data: { allowedRoles: [UserRole.superAdmin] },
    children: adminRoutes,
  },
];
